import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import { useScoreSidePanel } from "./ScoreSidePanelContext";
import useTheme from "../../../../common/hooks/useTheme";
import {
  getBiomarkerCategoryColor,
  getCategoryColor,
  enPrecisionTestKind,
  SCORE_CATEGORY_LABELS,
  isHighLowMetric,
  getHighLowLabel,
} from "../../store/helper";
import { Theme } from "../../../../providers/ThemeProvider";

// --- Biomarker category labels (independent of API score data) ---
export const BIOMARKER_CATEGORY_LABELS: { [key: number]: string } = {
  1: "Borderline",
  2: "Normal",
  3: "Optimal",
  4: "Out of range",
};

// Shown when a biomarker has no scoreCategory (0/None) — i.e. there is no
// reference range to evaluate it against, so it isn't scored.
export const NO_STATUS_LABEL = "No status available";

// --- Types ---
type PanelHeader = {
  name: string;
  fullName?: string;
  value: number;
  unit: string;
  score?: number;
  scoreCategory: number;
  categoryColor: string;
  categoryLabel: string;
  metricId?: string;
};

export type BiomarkerItem = {
  label: string;
  value: number;
  unit: string;
  weight: number | null;
  weightMax: number | null;
  categoryColor: string;
};

export type BreathMetricGroup = {
  metricName: string;
  weight: number | null;
  weightMax: number | null;
  categoryColor: string;
  biomarkers: { label: string; value: number; unit: string; categoryColor: string }[];
};

export type ProcessedWeight = {
  name: string;
  total: number;
  weightMax: number;
  description?: string;
  variant: "breath" | "other";
  biomarkerItems?: BiomarkerItem[];
  metricGroups?: BreathMetricGroup[];
};

type SystemPanelData = {
  type: "system";
  header: PanelHeader;
  system: {
    description?: string;
    processedWeights: ProcessedWeight[];
    whatDoesYourScoreMean: { label: string; description: string }[];
  };
};

type InfluencedByData = {
  positive: { label: string; description: string }[];
  negative: { label: string; description: string }[];
};

type MetricPanelData = {
  type: "metric";
  header: PanelHeader;
  metric: {
    description?: string;
    whyItIsImportantToTrackText?: string;
    howItsMeasuredText?: string;
    chartData: { value: number; dateCreated: string }[];
    id: string;
    unit: string;
    score: number;
    scoreCategory: number;
    categoryColor: string;
    influencedBy: InfluencedByData;
    lowerScoreImpacts: { label: string; description: string }[];
    habitsToLetGoTitle?: string;
    habitsToLetGo: any[];
  };
};

type BreathBiomarkerPanelData = {
  type: "breathBiomarker";
  header: PanelHeader;
  breathBiomarker: {
    description: string;
    chartData: { value: number; dateCreated: string }[];
    unit: string;
    id: string;
  };
};

type BloodBiomarkerPanelData = {
  type: "bloodBiomarker";
  header: PanelHeader;
  bloodBiomarker: {
    description: string;
    chartData: { value: number; dateCreated: string }[];
    unit: string;
    id: string;
    score: number;
    scoreCategory: number;
    categoryColor: string;
    influencedBy: InfluencedByData;
    affectsItems: { label: string; description: string }[];
  };
};

export type PanelData =
  | SystemPanelData
  | MetricPanelData
  | BreathBiomarkerPanelData
  | BloodBiomarkerPanelData;

export const usePanelData = () => {
  const theme = useTheme();
  const {
    contentType,
    metricData,
    breathBiomarkerData,
    bloodBiomarkerData,
    closePanel,
  } = useScoreSidePanel();

  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );
  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );
  const dashboardData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.dashboard
  );

  const systemData = (reportData as any)?.systems?.find(
    (group: any) => group.system === selectedSystem
  );

  let data: PanelData | null = null;

  switch (contentType) {
    case "system": {
      if (!systemData) break;
      const categoryColor = getCategoryColor(
        "%",
        systemData.score,
        theme,
        systemData.scoreCategory
      );

      // --- Process weights into ProcessedWeight[] ---
      const weights: any[] = systemData.weights || [];
      const processedWeights: ProcessedWeight[] = [];

      // Process kind 1 (Breath Resting) and kind 2 (Breath Active) separately
      const kind1Weight = weights.find((w: any) => w.kind === 1);
      const kind2Weight = weights.find((w: any) => w.kind === 2);

      const breathKinds: { weight: any; name: string }[] = [];
      if (kind1Weight) breathKinds.push({ weight: kind1Weight, name: "Breath Resting" });
      if (kind2Weight) breathKinds.push({ weight: kind2Weight, name: "Breath Active" });

      breathKinds.forEach(({ weight: kindWeight, name }) => {
        const breathMetricGroups: BreathMetricGroup[] = [];
        const kindNumber = kindWeight.kind;

        (systemData.breathBiomarkers || []).forEach((breathBio: any) => {
          const dashMetric = dashboardData?.metrics?.find(
            (m: any) => m.id === breathBio.id
          );

          if (!dashMetric) return;

          const scoreMetric = (systemData.breathBiomarkers || []).find(
            (m: any) => m.id === breathBio.id
          );

          // Filter nested biomarkers to only those matching this kind
          const biomarkers = (breathBio.biomarkers || [])
            .map((nested: any) => {
              const dashBio = dashboardData?.breathbiomarkers?.find(
                (b: any) => b.id === nested.id
              );
              if (!dashBio || Number(dashBio.kind) !== kindNumber) return null;
              const bioCategoryColor = getBiomarkerCategoryColor(
                dashBio.scoreCategory,
                theme,
                "biomarkers"
              );
              return {
                label: dashBio.label,
                value: dashBio.value,
                unit: dashBio.unit,
                categoryColor: bioCategoryColor,
              };
            })
            .filter(Boolean) as {
            label: string;
            value: number;
            unit: string;
            categoryColor: string;
          }[];

          if (biomarkers.length > 0) {
            const metricCategoryColor = getCategoryColor(
              "%",
              dashMetric.score,
              theme,
              dashMetric.scoreCategory
            );

            breathMetricGroups.push({
              metricName: dashMetric.label,
              weight: scoreMetric?.weight ?? null,
              weightMax: scoreMetric?.weightMax ?? null,
              categoryColor: metricCategoryColor,
              biomarkers,
            });
          }
        });

        processedWeights.push({
          name,
          total: kindWeight.weight || 0,
          weightMax: kindWeight.total || 0,
          description: kindWeight.description,
          variant: "breath",
          metricGroups: breathMetricGroups,
        });
      });

      // Process other weights (blood, body composition, etc.)
      const weightKinds = new Set(
        weights.filter((w: any) => w.kind !== 0).map((w: any) => Number(w.kind))
      );

      weights
        .filter((w: any) => w.kind !== 1 && w.kind !== 2 && w.kind !== 0)
        .forEach((weight: any) => {
          const biomarkerItems: BiomarkerItem[] = [];

          (systemData.bloodBiomarkers || []).forEach((bb: any) => {
            const dashBio = dashboardData?.bloodbiomarkers?.find(
              (d: any) => d.id === bb.id
            );
            if (!dashBio || Number(dashBio.kind) !== Number(weight.kind))
              return;

            const bioCategoryColor = getBiomarkerCategoryColor(
              dashBio.scoreCategory,
              theme,
              "biomarkers"
            );

            biomarkerItems.push({
              label: dashBio.shortname || dashBio.label,
              value: dashBio.value,
              unit: dashBio.unit,
              weight: bb.weight ?? null,
              weightMax: bb.weightMax ?? null,
              categoryColor: bioCategoryColor,
            });
          });

          processedWeights.push({
            name: weight.name,
            total: weight.weight,
            weightMax: weight.total || 0,
            description: weight.description,
            variant: "other",
            biomarkerItems,
          });
        });

      // Handle orphaned biomarkers whose kind has no matching weight entry
      const orphanedByKind: Record<number, BiomarkerItem[]> = {};
      (systemData.bloodBiomarkers || []).forEach((bb: any) => {
        const dashBio = dashboardData?.bloodbiomarkers?.find(
          (d: any) => d.id === bb.id
        );
        if (!dashBio) return;
        const kind = Number(dashBio.kind);
        if (weightKinds.has(kind)) return; // already handled by a weight entry

        const bioCategoryColor = getBiomarkerCategoryColor(
          dashBio.scoreCategory,
          theme,
          "biomarkers"
        );

        if (!orphanedByKind[kind]) orphanedByKind[kind] = [];
        orphanedByKind[kind].push({
          label: dashBio.shortname || dashBio.label,
          value: dashBio.value,
          unit: dashBio.unit,
          weight: bb.weight ?? null,
          weightMax: bb.weightMax ?? null,
          categoryColor: bioCategoryColor,
        });
      });

      Object.entries(orphanedByKind).forEach(([kind, biomarkerItems]) => {
        processedWeights.push({
          name: enPrecisionTestKind[Number(kind)] || "Other",
          total: 0,
          weightMax: 0,
          variant: "other",
          biomarkerItems,
        });
      });

      data = {
        type: "system",
        header: {
          name: systemData.name,
          value: systemData.score,
          unit: "%",
          scoreCategory: systemData.scoreCategory,
          categoryColor,
          categoryLabel:
            SCORE_CATEGORY_LABELS[systemData.scoreCategory || 1] || "",
        },
        system: {
          description: systemData.description,
          processedWeights,
          whatDoesYourScoreMean: systemData.whatDoesYourScoreMean || [],
        },
      };
      break;
    }

    case "metric": {
      if (!metricData) break;
      const categoryColor = getCategoryColor(
        "%",
        metricData.score,
        theme,
        metricData.scoreCategory
      );
      data = {
        type: "metric",
        header: {
          name: metricData.name,
          value: metricData.value,
          unit: metricData.unit,
          scoreCategory: metricData.scoreCategory,
          categoryColor,
          categoryLabel: isHighLowMetric(metricData.id)
            ? getHighLowLabel(metricData.score)
            : SCORE_CATEGORY_LABELS[metricData.scoreCategory || 1] || "",
          metricId: metricData.id,
        },
        metric: {
          description: metricData.description,
          whyItIsImportantToTrackText: metricData.whyItIsImportantToTrackText,
          howItsMeasuredText: metricData.howItsMeasuredText,
          chartData: metricData.values || [],
          id: metricData.id,
          unit: metricData.unit,
          score: metricData.score,
          scoreCategory: metricData.scoreCategory,
          categoryColor,
          influencedBy: {
            positive: metricData.influencedBy?.positive || [],
            negative: metricData.influencedBy?.negative || [],
          },
          lowerScoreImpacts:
            metricData.affectsThis || systemData?.lowerScoreImpacts || [],
          habitsToLetGoTitle: systemData?.habitsToLetGoTitle,
          habitsToLetGo: systemData?.habitsToLetGo || [],
        },
      };
      break;
    }

    case "breathBiomarker": {
      if (!breathBiomarkerData) break;
      const categoryColor = getBiomarkerCategoryColor(
        breathBiomarkerData.scoreCategory,
        theme,
        "biomarkers"
      );

      data = {
        type: "breathBiomarker",
        header: {
          name: breathBiomarkerData.label,
          value: breathBiomarkerData.value,
          unit: breathBiomarkerData.unit,
          scoreCategory: breathBiomarkerData.scoreCategory,
          categoryColor,
          categoryLabel:
            BIOMARKER_CATEGORY_LABELS[breathBiomarkerData.scoreCategory] || "",
        },
        breathBiomarker: {
          description:
            breathBiomarkerData.description || "No description provided",
          chartData: breathBiomarkerData.values || [],
          unit: breathBiomarkerData.unit,
          id: breathBiomarkerData.id,
        },
      };
      break;
    }

    case "bloodBiomarker": {
      if (!bloodBiomarkerData) break;

      const categoryColor = getBiomarkerCategoryColor(
        bloodBiomarkerData.scoreCategory,
        theme,
        "biomarkers"
      );

      data = {
        type: "bloodBiomarker",
        header: {
          name: bloodBiomarkerData.label,
          fullName: bloodBiomarkerData.fullName,
          value: bloodBiomarkerData.value,
          unit: bloodBiomarkerData.unit,
          scoreCategory: bloodBiomarkerData.scoreCategory,
          categoryColor,
          categoryLabel:
            BIOMARKER_CATEGORY_LABELS[bloodBiomarkerData.scoreCategory] || "",
        },
        bloodBiomarker: {
          description: bloodBiomarkerData.description || "",
          chartData: bloodBiomarkerData.values || [],
          unit: bloodBiomarkerData.unit,
          id: bloodBiomarkerData.id,
          score: bloodBiomarkerData.score,
          scoreCategory: bloodBiomarkerData.scoreCategory,
          categoryColor,
          influencedBy: {
            positive: bloodBiomarkerData.influencedBy?.positive || [],
            negative: bloodBiomarkerData.influencedBy?.negative || [],
          },
          affectsItems:
            bloodBiomarkerData.affectsThis ||
            systemData?.lowerScoreImpacts ||
            [],
        },
      };
      break;
    }
  }

  return { data, theme, closePanel };
};
